import React, { useState, useEffect, useRef } from "react";
import { Fuel, Wallet, BarChart3, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, History, Save } from "lucide-react";
import { toast } from "react-toastify";
import { useLocation, useSearchParams } from "react-router-dom";
import Loader from "../Loader";
import { useLanguage } from "../../context/LanguageContext";
import { API } from "../../utils";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const DailyEntry = () => {
  const { t } = useLanguage();
  const isFirstRender = useRef(true);
  const pageTopRef = useRef(null);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState({
    petrolPrice: 0,
    dieselPrice: 0,
    oilPrices: { "20ml": 0, "40ml": 0, "60ml": 0 }
  });

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [shift, setShift] = useState("Intermediate Shift");
  const [auditInfo, setAuditInfo] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [showRecent, setShowRecent] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(4);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState({ show: false, type: "", pendingValue: null });
  const [carriedOver, setCarriedOver] = useState(null);
  const [missingIntermediate, setMissingIntermediate] = useState(false);
  const [intermediateAmount, setIntermediateAmount] = useState(0);
  const [intermediatePayment, setIntermediatePayment] = useState(null);
  const [intermediateEntry, setIntermediateEntry] = useState(null);
  const [showInterPayment, setShowInterPayment] = useState(false);
  const [dailySummary, setDailySummary] = useState(null);
  const [todayPricing, setTodayPricing] = useState(null);
  const [snapshotPricing, setSnapshotPricing] = useState(null);
  const [priceMismatch, setPriceMismatch] = useState(false);
  const [showPriceWarning, setShowPriceWarning] = useState(false);

  const fetchRecent = async (isPagination = false) => {
    if (!isPagination) setLoading(true);
    try {
      const response = await API.get(`/api/daily-entry?page=${page}&limit=${limit}`);
      const result = response.data;
      if (result.success) {
        setRecentEntries(result.data);
        setTotalPages(result.totalPages || 1);
        setTotalCount(result.totalCount || result.data.length);
      }
    } catch (err) {
      console.error("Error fetching recent entries", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailySummary = async (dateVal) => {
    try {
      const response = await API.get(`/api/daily-summary?date=${dateVal}`);
      const result = response.data;
      if (result.success && result.data) {
        setDailySummary(result.data);
      } else {
        setDailySummary(null);
      }
    } catch (err) {
      console.error("Error fetching daily summary", err);
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      fetchRecent(false);
      isFirstRender.current = false;
    } else {
      fetchRecent(true);
    }
  }, [page, limit]);

  useEffect(() => {
    const d = searchParams.get("date");
    const s = searchParams.get("shift");
    if (d && d !== "undefined" && d !== date) setDate(d);
    if (s && s !== "undefined" && s !== shift) setShift(s);
  }, [searchParams]);

  const [petrol, setPetrol] = useState({ m1Open: 0, m1Close: 0, m2Open: 0, m2Close: 0, testFuel: 0 });
  const [diesel, setDiesel] = useState({ m1Open: 0, m1Close: 0, m2Open: 0, m2Close: 0, testFuel: 0 });
  
  const [oil, setOil] = useState({
    "20ml": { carryFwd: 0, add: 0, open: 0, close: 0 },
    "40ml": { carryFwd: 0, add: 0, open: 0, close: 0 },
    "60ml": { carryFwd: 0, add: 0, open: 0, close: 0 }
  });

  const [payment, setPayment] = useState({ cash: 0, online: 0, credit: 0, receipt: 0 });

  useEffect(() => {
    const fetchExisting = async () => {
      try {
        setSnapshotPricing(null);
        setPriceMismatch(false);
        setShowPriceWarning(false);
        const response = await API.get(`/api/daily-entry/check?date=${date}&shift=${shift}`);
        const result = response.data;
        // If entry exists, also fetch saved price snapshot for that date
        if (result.success && result.data) {
          const localDate = new Date(result.data.date);

          const entryDate =
            localDate.getFullYear() +
            "-" +
            String(localDate.getMonth() + 1).padStart(2, "0") +
            "-" +
            String(localDate.getDate()).padStart(2, "0");
                  
          try {
            const snapRes = await API.get(`/api/daily-entry/snapshot/${entryDate}`);
            const snapResult = snapRes.data;

            if (snapResult.success && snapResult.data && todayPricing) {

              setSnapshotPricing(snapResult.data);

              const mismatch =
              snapResult.data.petrol !== todayPricing?.petrolPrice ||
              snapResult.data.diesel !== todayPricing?.dieselPrice ||
              snapResult.data.oil20ml !== todayPricing?.oilPrices?.["20ml"] ||
              snapResult.data.oil40ml !== todayPricing?.oilPrices?.["40ml"] ||
              snapResult.data.oil60ml !== todayPricing?.oilPrices?.["60ml"];
              console.log("MISMATCH", mismatch);
              if (mismatch) {
                setPriceMismatch(true);
                toast.warn(
                  "Saved date pricing differs from today's pricing",
                  { autoClose: 6000 }
                );

              } else {

                setPriceMismatch(false);

              }

              // AFTER comparison
              setPricing({
              petrolPrice: snapResult.data.petrol,
              dieselPrice: snapResult.data.diesel,
              oilPrices: {
                "20ml": snapResult.data.oil20ml,
                "40ml": snapResult.data.oil40ml,
                "60ml": snapResult.data.oil60ml
              }
            });
            } else {
                setSnapshotPricing(null);
                setPriceMismatch(false);
            }
          } catch (snapErr) {
            console.error('Error fetching price snapshot', snapErr);
          }
        }

        if (result.success && result.data) {
          const entry = result.data;
          setPetrol({ ...entry.petrol });
          setDiesel({ ...entry.diesel });
          setOil({
            "20ml": { carryFwd: entry.oil?.["20ml"]?.carryFwd || 0, add: entry.oil?.["20ml"]?.add || 0, open: entry.oil?.["20ml"]?.open || 0, close: entry.oil?.["20ml"]?.close || 0 },
            "40ml": { carryFwd: entry.oil?.["40ml"]?.carryFwd || 0, add: entry.oil?.["40ml"]?.add || 0, open: entry.oil?.["40ml"]?.open || 0, close: entry.oil?.["40ml"]?.close || 0 },
            "60ml": { carryFwd: entry.oil?.["60ml"]?.carryFwd || 0, add: entry.oil?.["60ml"]?.add || 0, open: entry.oil?.["60ml"]?.open || 0, close: entry.oil?.["60ml"]?.close || 0 }
          });
          setPayment({ ...entry.payment, receipt: entry.payment?.receipt || 0 });
          setCarriedOver(null);
          setMissingIntermediate(result.intermediateMissing || false);
          setIntermediateAmount(result.intermediateAmount || 0);
          setIntermediatePayment(result.intermediatePayment || null);
          setIntermediateEntry(result.intermediateEntry || null);
          setAuditInfo({
            createdBy: entry.createdBy,
            createdAt: entry.createdAt,
            updatedBy: entry.updatedBy,
            updatedAt: entry.updatedAt
          });
        } else if (result.success && result.carriedOver) {
          const co = result.carriedOver;
          setPetrol({ m1Open: co.petrol?.m1Close || 0, m1Close: 0, m2Open: co.petrol?.m2Close || 0, m2Close: 0, testFuel: 0 });
          setDiesel({ m1Open: co.diesel?.m1Close || 0, m1Close: 0, m2Open: co.diesel?.m2Close || 0, m2Close: 0, testFuel: 0 });
          setOil({ 
            "20ml": { carryFwd: co.oil?.["20ml"]?.close || 0, add: 0, open: co.oil?.["20ml"]?.close || 0, close: 0 }, 
            "40ml": { carryFwd: co.oil?.["40ml"]?.close || 0, add: 0, open: co.oil?.["40ml"]?.close || 0, close: 0 }, 
            "60ml": { carryFwd: co.oil?.["60ml"]?.close || 0, add: 0, open: co.oil?.["60ml"]?.close || 0, close: 0 } 
          });
          setPayment({ cash: 0, online: 0, credit: 0, receipt: 0 });
          setCarriedOver(co);
          setAuditInfo(null);
          setIntermediateAmount(result.intermediateAmount || 0);
          setIntermediatePayment(result.intermediatePayment || null);
          setIntermediateEntry(result.intermediateEntry || null);
          
          if (result.intermediateMissing) {
            setMissingIntermediate(true);
            toast.warn("Warning: Intermediate Shift for today is missing", { autoClose: 6000 });
          } else {
            setMissingIntermediate(false);
          }
        } else {
          setPetrol({ m1Open: 0, m1Close: 0, m2Open: 0, m2Close: 0, testFuel: 0 });
          setDiesel({ m1Open: 0, m1Close: 0, m2Open: 0, m2Close: 0, testFuel: 0 });
          setOil({ 
            "20ml": { carryFwd: 0, add: 0, open: 0, close: 0 }, 
            "40ml": { carryFwd: 0, add: 0, open: 0, close: 0 }, 
            "60ml": { carryFwd: 0, add: 0, open: 0, close: 0 } 
          });
          setPayment({ cash: 0, online: 0, credit: 0, receipt: 0 });
          setCarriedOver(null);
          setMissingIntermediate(result.intermediateMissing || false);
          if (result.intermediateMissing) {
            toast.warn("Warning: Intermediate Shift for today is missing", { autoClose: 6000 });
          }
          setIntermediateAmount(result.intermediateAmount || 0);
          setIntermediatePayment(result.intermediatePayment || null);
          setIntermediateEntry(result.intermediateEntry || null);
          setAuditInfo(null);
        }
      } catch (err) {
        console.error("Error fetching existing entry", err);
      }
    };

    fetchExisting();
    if (shift === "Day Shift") {
      fetchDailySummary(date);
    } else {
      setDailySummary(null);
    }
  }, [date, shift, todayPricing]);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await API.get("/api/pricing");
        const result = response.data;

        if (result.success && result.data) {
          setPricing(result.data);

          // store today's pricing separately
          setTodayPricing(result.data);
        }

      } catch (err) {
        console.error("Error fetching pricing", err);
      }
    };

    fetchPricing();
  }, []);

  const calculateFuel = (fuelData, price) => {
    let sold = 0;
    if (fuelData.m1Close !== "" && fuelData.m1Close !== null && fuelData.m2Close !== "" && fuelData.m2Close !== null) {
      sold = Math.max(0, (Number(fuelData.m1Close) - Number(fuelData.m1Open)) + (Number(fuelData.m2Close) - Number(fuelData.m2Open)) - Number(fuelData.testFuel));
    }
    const amount = sold * price;
    return { sold, amount };
  };

  const calculateOilItem = (oilData, price) => {
    let effectiveOpen = Number(oilData.open);
    if (oilData.carryFwd !== undefined || oilData.add !== undefined) {
       effectiveOpen = Number(oilData.carryFwd || 0) + Number(oilData.add || 0);
    }
    
    if (oilData.close === "" || oilData.close === null || oilData.close === undefined) {
      return { sold: 0, amount: 0, effectiveOpen };
    }
    
    const sold = Math.max(0, effectiveOpen - Number(oilData.close));
    const amount = sold * price;
    return { sold, amount, effectiveOpen };
  };

  const pData = calculateFuel(petrol, pricing.petrolPrice);
  const dData = calculateFuel(diesel, pricing.dieselPrice);
  
  const o20Data = calculateOilItem(oil["20ml"], pricing.oilPrices["20ml"]);
  const o40Data = calculateOilItem(oil["40ml"], pricing.oilPrices["40ml"]);
  const o60Data = calculateOilItem(oil["60ml"], pricing.oilPrices["60ml"]);
  
  const oilTotalAmount = o20Data.amount + o40Data.amount + o60Data.amount;
  const salesTotal = pData.amount + dData.amount + oilTotalAmount;
  const paymentTotal = Number(payment.cash) + Number(payment.online) + Number(payment.credit);
  const finalCollectionAmount = salesTotal + Number(payment.receipt || 0);

  const isMatched = Math.abs(salesTotal - paymentTotal) <= 1;

  const isEditable = (dateStr) => {
    const selected = new Date(dateStr);
    selected.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diffTime = now - selected;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 3;
  };

  const editable = isEditable(date);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!editable) {
      toast.error(t("dailyEntry.errorReadOnly"));
      return;
    }
    if (!isMatched) {
      toast.error(t("dailyEntry.errorMismatch"));
      return;
    }

    if (
      Number(petrol.m1Close) < Number(petrol.m1Open) || 
      Number(petrol.m2Close) < Number(petrol.m2Open) ||
      Number(diesel.m1Close) < Number(diesel.m1Open) ||
      Number(diesel.m2Close) < Number(diesel.m2Open)
    ) {
      toast.error("Petrol/Diesel closing value cannot be less than opening value!");
      return;
    }

    if (
      Number(oil["20ml"].close) > (Number(oil["20ml"].carryFwd) + Number(oil["20ml"].add)) ||
      Number(oil["40ml"].close) > (Number(oil["40ml"].carryFwd) + Number(oil["40ml"].add)) ||
      Number(oil["60ml"].close) > (Number(oil["60ml"].carryFwd) + Number(oil["60ml"].add))
    ) {
      toast.error("Oil closing value cannot be greater than the total opening value!");
      return;
    }

    if (auditInfo) {
      setShowConfirmModal({ show: true, type: "save", pendingValue: null });
      return;
    }
    proceedSave();
  };

  const proceedSave = async () => {
    setIsSaving(true);
    const payload = {
      date,
      shift: shift || "Day Shift",
      petrol: { ...petrol, sold: pData.sold, amount: pData.amount },
      diesel: { ...diesel, sold: dData.sold, amount: dData.amount },
      oil: {
        "20ml": { carryFwd: Number(oil["20ml"].carryFwd), add: Number(oil["20ml"].add), open: o20Data.effectiveOpen, close: Number(oil["20ml"].close), sold: o20Data.sold, amount: o20Data.amount },
        "40ml": { carryFwd: Number(oil["40ml"].carryFwd), add: Number(oil["40ml"].add), open: o40Data.effectiveOpen, close: Number(oil["40ml"].close), sold: o40Data.sold, amount: o40Data.amount },
        "60ml": { carryFwd: Number(oil["60ml"].carryFwd), add: Number(oil["60ml"].add), open: o60Data.effectiveOpen, close: Number(oil["60ml"].close), sold: o60Data.sold, amount: o60Data.amount },
      },
      payment: { cash: Number(payment.cash), online: Number(payment.online), credit: Number(payment.credit), receipt: Number(payment.receipt || 0) },
      totals: { petrol: pData.amount, diesel: dData.amount, oil: oilTotalAmount, grandTotal: salesTotal, salesTotal, finalCollectionAmount },    
      priceSnapshot: {
        petrol: pricing.petrolPrice,
        diesel: pricing.dieselPrice,
        oil: pricing.oilPrices["20ml"],
        oil20ml: pricing.oilPrices["20ml"],
        oil40ml: pricing.oilPrices["40ml"],
        oil60ml: pricing.oilPrices["60ml"]
      }
    };

    try {
      const response = await API.post("/api/daily-entry", payload);
      const result = response.data;
      if (result.success) {
        toast.success(t("common.success"));
        const entry = result.data;
        setAuditInfo({ createdBy: entry.createdBy, createdAt: entry.createdAt, updatedBy: entry.updatedBy, updatedAt: entry.updatedAt });
        fetchRecent(true);
        if (shift === "Day Shift") {
          fetchDailySummary(date);
        }
      } else {
        toast.error(result.error || t("common.error"));
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || t("common.error");
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
      setShowConfirmModal({ show: false, type: "", pendingValue: null });
    }
  };

  const handleInputChange = (setter) => (e) => {
    const { name, value } = e.target;
    setter(prev => ({ ...prev, [name]: value }));
  };

  const handleOilChange = (size, field) => (e) => {
    const val = e.target.value;
    setOil(prev => ({ ...prev, [size]: { ...prev[size], [field]: val } }));
  };

  const handleConfirmModalAction = async () => {

  const pendingData = showConfirmModal.pendingValue;
  const actionType = showConfirmModal.type;

      /* CLOSE MODAL */
      setShowConfirmModal({
        show: false,
        type: "",
        pendingValue: null,
      });

      /* DATE CHANGE */
      if (actionType === "date") {

        setDate(pendingData);

        setTimeout(() => {

        pageTopRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

        }, 300);

        return;
      }

      /* SHIFT CHANGE */
      if (actionType === "shift") {

        setShift(pendingData);

        setTimeout(() => {

        pageTopRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

        }, 300);

        return;
      }

      /* SAVE */
      if (actionType === "save") {

        proceedSave();

        return;
      }

      /* LOAD ENTRY */
      if (actionType === "load" && pendingData) {

        const entry = pendingData;

        /* ONLY CHANGE DATE + SHIFT */
        /* useEffect already loads data automatically */

        setDate(
          new Date(entry.date)
            .toISOString()
            .split("T")[0]
        );

        setShift(entry.shift || "Day Shift");

        /* WAIT FOR REACT RENDER */
        setTimeout(() => {
    pageTopRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

        }, 500);

        return;
      }

    };
  const getOpenInputClass = (currentVal, originalVal, defaultFocus) => {
    const base = "w-full border border-gray-200 rounded p-1.5 text-xs outline-none disabled:bg-gray-50 disabled:text-gray-400";
    if (carriedOver && String(currentVal) !== String(originalVal)) {
      return `${base} ring-2 ring-yellow-400 border-yellow-400`;
    }
    return `${base} ${defaultFocus}`;
  };

  return (
    <div ref={pageTopRef} className="max-w-7xl mx-auto space-y-6 pb-10 font-sans">
      {(loading || isSaving) && <Loader message={isSaving ? t("dailyEntry.saving") : "KAVIRAJ AGENCIES"} />}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3">
          <button onClick={() => setShowRecent(!showRecent)} className="p-1 hover:bg-gray-100 rounded-md transition-colors text-gray-500">
            {showRecent ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
          </button>
          <h2 className="text-xl font-bold text-[#1e293b]">{t("dailyEntry.title")}</h2>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-6 text-[13px] font-semibold text-gray-700">
          {!editable && <div className="bg-red-50 text-red-700 px-3 py-1 rounded-md text-[11px] border border-red-200 animate-pulse w-full sm:w-auto text-center">{t("dailyEntry.readOnly")}</div>}
          <div className="flex items-center space-x-2">
            <span>{t("common.date")}:</span>
            <input 
              type="date" 
              className="border border-gray-300 rounded px-2 py-1 bg-white outline-none text-xs" 
              value={date} 
              onChange={(e) => setShowConfirmModal({ show: true, type: "date", pendingValue: e.target.value })} 
            />
          </div>
          <div className="flex items-center space-x-2">
            <span>{t("common.shift")}:</span>
            <select 
              className="border border-gray-300 rounded px-2 py-1 bg-white outline-none text-xs" 
              value={shift} 
              onChange={(e) => setShowConfirmModal({ show: true, type: "shift", pendingValue: e.target.value })}
            >
              <option value="Intermediate Shift">Intermediate Shift</option>
              <option value="Day Shift">Day Shift</option>
            </select>
          </div>
        </div>
      </div>

      {missingIntermediate && (
        <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 px-4 py-3 rounded-lg flex items-start sm:items-center space-x-3 mb-4 shadow-sm animate-pulse">
          <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-bold">Missing Intermediate Shift</h4>
            <p className="text-xs mt-0.5 text-yellow-700">
              No Intermediate Shift found for today. The opening values below have been carried over from yesterday's Day Shift. Please switch to Intermediate Shift if you need to enter it first!
            </p>
          </div>
        </div>
      )}

      {auditInfo && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 flex flex-wrap gap-x-8 gap-y-1 text-[11px] text-gray-500 italic">
          <div className="flex items-center space-x-1">
            <span className="font-bold text-gray-400 uppercase tracking-tighter">{t("dailyEntry.auditCreated")}</span>
            <span>{auditInfo.createdBy || 'System'}</span>
            <span className="text-gray-300">|</span>
            <span>{new Date(auditInfo.createdAt).toLocaleString()}</span>
          </div>
          {auditInfo.updatedAt !== auditInfo.createdAt && (
            <div className="flex items-center space-x-1">
              <span className="font-bold text-gray-400 uppercase tracking-tighter">{t("dailyEntry.auditLastUpdate")}</span>
              <span>{auditInfo.updatedBy || auditInfo.createdBy || 'System'}</span>
              <span className="text-gray-300">|</span>
              <span>{new Date(auditInfo.updatedAt).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}
      {/*mismatch price */}
    
      {/* PRICE WARNING */}
      {priceMismatch && snapshotPricing && todayPricing && (
        <div className="mb-4 rounded-[28px] border border-amber-200/60 bg-white/70 backdrop-blur-2xl shadow-[0_10px_40px_rgba(0,0,0,0.06)] overflow-hidden relative">

          {/* glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-amber-100/20 via-yellow-100/10 to-orange-100/20 pointer-events-none"></div>

          {/* top glossy layer */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/50 via-white/10 to-transparent pointer-events-none"></div>

          {/* header */}
          <button
            type="button"
            onClick={() => setShowPriceWarning(!showPriceWarning)}
            className="w-full flex items-center justify-between px-4 py-3 relative z-10 transition-all duration-300 hover:bg-white/20"
          >

            {/* left */}
            <div className="flex items-center gap-3">

              {/* icon */}
              <div className="relative w-10 h-10 rounded-2xl bg-amber-100/80 backdrop-blur-xl border border-white/40 flex items-center justify-center shadow-[0_4px_20px_rgba(251,191,36,0.25)]">

                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/40 to-transparent"></div>

                <AlertCircle className="w-5 h-5 text-amber-600 relative z-10" />

              </div>

              {/* text */}
              <div className="text-left">

                <h3 className="text-[15px] font-semibold text-gray-900 tracking-tight">
                    Price Changed
                </h3>

                {!showPriceWarning && (
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Old vs current pricing
                  </p>
                )}

              </div>

            </div>

            {/* arrow */}
            <div className="w-8 h-8 rounded-xl bg-white/70 backdrop-blur-xl border border-white/50 flex items-center justify-center shadow-sm">

              {showPriceWarning ? (
                <ChevronUp className="w-4 h-4 text-gray-700" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-700" />
              )}

            </div>

          </button>

          {/* collapse */}
          <div
            className={`
              overflow-hidden
              transition-all
              duration-500
              ease-out
              backdrop-blur-2xl
              ${
                showPriceWarning
                  ? "max-h-[500px] opacity-100 border-t border-white/40 bg-white/20"
                  : "max-h-0 opacity-0"
              }
            `}
          >

            {/* cards */}
            <div className="grid grid-cols-2 gap-3 p-3">

              {/* old */}
              <div className="rounded-[24px] bg-white/60 backdrop-blur-2xl border border-white/50 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.05)] relative overflow-hidden">

                {/* glossy */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/10 to-transparent pointer-events-none"></div>

                <div className="relative z-10">

                  <div className="flex items-center justify-between mb-3">

                    <span className="text-[11px] font-semibold text-gray-500 uppercase">
                      Saved
                    </span>

                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200/70 backdrop-blur-xl text-gray-600 font-semibold">
                      OLD
                    </span>

                  </div>

                  <div className="space-y-2 text-sm">

                    <div className="flex justify-between">
                      <span className="text-gray-500">Petrol</span>
                      <span className="font-semibold text-gray-900">
                        ₹{snapshotPricing?.petrol}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500">Diesel</span>
                      <span className="font-semibold text-gray-900">
                        ₹{snapshotPricing?.diesel}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500">20ml</span>
                      <span className="font-semibold text-gray-900">
                        ₹{snapshotPricing?.oil20ml}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500">40ml</span>
                      <span className="font-semibold text-gray-900">
                        ₹{snapshotPricing?.oil40ml}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500">60ml</span>
                      <span className="font-semibold text-gray-900">
                        ₹{snapshotPricing?.oil60ml}
                      </span>
                    </div>

                  </div>

                </div>

              </div>

              {/* current */}
              <div className="rounded-[24px] bg-emerald-50/50 backdrop-blur-2xl border border-white/50 p-3 shadow-[0_8px_30px_rgba(16,185,129,0.12)] relative overflow-hidden">

                {/* glossy */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/10 to-transparent pointer-events-none"></div>

                <div className="relative z-10">

                  <div className="flex items-center justify-between mb-3">

                    <span className="text-[11px] font-semibold text-emerald-700 uppercase">
                      Current
                    </span>

                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-200/70 backdrop-blur-xl text-emerald-700 font-semibold">
                      LIVE
                    </span>

                  </div>

                  <div className="space-y-2 text-sm">

                    <div className="flex justify-between">
                      <span className="text-gray-500">Petrol</span>

                      <span className={`font-semibold ${
                        snapshotPricing?.petrol !== todayPricing?.petrolPrice
                          ? "text-red-500"
                          : "text-gray-900"
                      }`}>
                        ₹{todayPricing?.petrolPrice}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500">Diesel</span>

                      <span className={`font-semibold ${
                        snapshotPricing?.diesel !== todayPricing?.dieselPrice
                          ? "text-red-500"
                          : "text-gray-900"
                      }`}>
                        ₹{todayPricing?.dieselPrice}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500">20ml</span>

                      <span className={`font-semibold ${
                        snapshotPricing?.oil20ml !== todayPricing?.oilPrices?.["20ml"]
                          ? "text-red-500"
                          : "text-gray-900"
                      }`}>
                        ₹{todayPricing?.oilPrices?.["20ml"]}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500">40ml</span>

                      <span className={`font-semibold ${
                        snapshotPricing?.oil40ml !== todayPricing?.oilPrices?.["40ml"]
                          ? "text-red-500"
                          : "text-gray-900"
                      }`}>
                        ₹{todayPricing?.oilPrices?.["40ml"]}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-gray-500">60ml</span>

                      <span className={`font-semibold ${
                        snapshotPricing?.oil60ml !== todayPricing?.oilPrices?.["60ml"]
                          ? "text-red-500"
                          : "text-gray-900"
                      }`}>
                        ₹{todayPricing?.oilPrices?.["60ml"]}
                      </span>
                    </div>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>
      )}
      {/* TOP ROW: 3 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* PETROL CARD */}
        <div className="bg-white/70 backdrop-blur-2xl border border-white/50 rounded-[30px] shadow-[0_10px_40px_rgba(0,0,0,0.06)] overflow-hidden border-green-200 overflow-hidden flex flex-col h-full">
          <div className="px-4 py-3 flex items-center space-x-2 border-b border-green-200 bg-[#f0fdf4]">
            <Fuel className="w-5 h-5 text-green-600" />
            <h3 className="text-[13px] font-bold text-green-700 tracking-wide uppercase">{t("common.petrol")}</h3>
          </div>
          <div className="p-4 space-y-4 flex-grow">
            <div>
              <p className="text-[12px] font-bold text-gray-800 mb-2 flex justify-between items-center">
                <span>{t("dailyEntry.machine1")}</span>
                {shift === "Day Shift" && intermediateEntry && (
                   <span className="text-[10px] text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                     Int. Opening: {intermediateEntry.petrol?.m1Open || 0}
                   </span>
                )}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] text-gray-600 font-semibold block mb-1">{t("dailyEntry.opening")}</label>
                  <input type="number" name="m1Open" value={petrol.m1Open || ''} onChange={handleInputChange(setPetrol)} disabled={!editable} className={getOpenInputClass(petrol.m1Open, carriedOver?.petrol?.m1Close, "focus:border-green-500")} />
                </div>
                <div>
                  <label className="text-[11px] text-gray-600 font-semibold block mb-1">{t("dailyEntry.closing")}</label>
                  <input type="number" name="m1Close" value={petrol.m1Close || ''} onChange={handleInputChange(setPetrol)} disabled={!editable} className="w-full border border-gray-200 rounded p-1.5 text-xs outline-none focus:border-green-500 disabled:bg-gray-50 disabled:text-gray-400" />
                </div>
              </div>
            </div>
            
            <div>
              <p className="text-[12px] font-bold text-gray-800 mb-2 mt-3 flex justify-between items-center">
                <span>{t("dailyEntry.machine2")}</span>
                {shift === "Day Shift" && intermediateEntry && (
                   <span className="text-[10px] text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                     Int. Opening: {intermediateEntry.petrol?.m2Open || 0}
                   </span>
                )}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] text-gray-600 font-semibold block mb-1">{t("dailyEntry.opening")}</label>
                  <input type="number" name="m2Open" value={petrol.m2Open || ''} onChange={handleInputChange(setPetrol)} disabled={!editable} className={getOpenInputClass(petrol.m2Open, carriedOver?.petrol?.m2Close, "focus:border-green-500")} />
                </div>
                <div>
                  <label className="text-[11px] text-gray-600 font-semibold block mb-1">{t("dailyEntry.closing")}</label>
                  <input type="number" name="m2Close" value={petrol.m2Close || ''} onChange={handleInputChange(setPetrol)} disabled={!editable} className="w-full border border-gray-200 rounded p-1.5 text-xs outline-none focus:border-green-500 disabled:bg-gray-50 disabled:text-gray-400" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <label className="text-[11px] text-gray-600 font-semibold block mb-1">{t("dailyEntry.testFuel")}</label>
                <input type="number" name="testFuel" value={petrol.testFuel || ''} onChange={handleInputChange(setPetrol)} disabled={!editable} className="w-full border border-gray-200 rounded p-1.5 text-xs outline-none focus:border-green-500 disabled:bg-gray-50 disabled:text-gray-400" />
              </div>
            </div>
          </div>
          
          <div className="bg-[#f0fdf4] border-t border-green-200 flex p-3 text-center">
            <div className="flex-1 border-r border-green-200">
              <p className="text-[11px] text-gray-600 font-semibold mb-1">{t("dailyEntry.petrolSold")}</p>
              <p className="text-[15px] font-bold text-green-600 whitespace-nowrap">{pData.sold > 0 ? pData.sold.toFixed(2) : "0.00"}</p>
            </div>
            <div className="flex-1">
              <p className="text-[11px] text-gray-600 font-semibold mb-1">{t("dailyEntry.petrolAmount")}</p>
              <p className="text-[15px] font-bold text-green-600 whitespace-nowrap">{pData.amount > 0 ? pData.amount.toLocaleString('en-IN', {minimumFractionDigits: 2}) : "0.00"}</p>
            </div>
          </div>
        </div>

        {/* DIESEL CARD */}
        <div className="bg-white/70 backdrop-blur-2xl border border-white/50 rounded-[30px] shadow-[0_10px_40px_rgba(0,0,0,0.06)] overflow-hidden border-blue-200 overflow-hidden flex flex-col h-full">
          <div className="px-4 py-3 flex items-center space-x-2 border-b border-blue-200 bg-[#eff6ff]">
            <Fuel className="w-5 h-5 text-blue-600" />
            <h3 className="text-[13px] font-bold text-blue-700 tracking-wide uppercase">{t("common.diesel")}</h3>
          </div>
          <div className="p-4 space-y-4 flex-grow">
            <div>
              <p className="text-[12px] font-bold text-gray-800 mb-2 flex justify-between items-center">
                <span>{t("dailyEntry.machine1")}</span>
                {shift === "Day Shift" && intermediateEntry && (
                   <span className="text-[10px] text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                     Int. Opening: {intermediateEntry.diesel?.m1Open || 0}
                   </span>
                )}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] text-gray-600 font-semibold block mb-1">{t("dailyEntry.opening")}</label>
                  <input type="number" name="m1Open" value={diesel.m1Open || ''} onChange={handleInputChange(setDiesel)} disabled={!editable} className={getOpenInputClass(diesel.m1Open, carriedOver?.diesel?.m1Close, "focus:border-blue-500")} />
                </div>
                <div>
                  <label className="text-[11px] text-gray-600 font-semibold block mb-1">{t("dailyEntry.closing")}</label>
                  <input type="number" name="m1Close" value={diesel.m1Close || ''} onChange={handleInputChange(setDiesel)} disabled={!editable} className="w-full border border-gray-200 rounded p-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400" />
                </div>
              </div>
            </div>
            
            <div>
              <p className="text-[12px] font-bold text-gray-800 mb-2 mt-3 flex justify-between items-center">
                <span>{t("dailyEntry.machine2")}</span>
                {shift === "Day Shift" && intermediateEntry && (
                   <span className="text-[10px] text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                     Int. Opening: {intermediateEntry.diesel?.m2Open || 0}
                   </span>
                )}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] text-gray-600 font-semibold block mb-1">{t("dailyEntry.opening")}</label>
                  <input type="number" name="m2Open" value={diesel.m2Open || ''} onChange={handleInputChange(setDiesel)} disabled={!editable} className={getOpenInputClass(diesel.m2Open, carriedOver?.diesel?.m2Close, "focus:border-blue-500")} />
                </div>
                <div>
                  <label className="text-[11px] text-gray-600 font-semibold block mb-1">{t("dailyEntry.closing")}</label>
                  <input type="number" name="m2Close" value={diesel.m2Close || ''} onChange={handleInputChange(setDiesel)} disabled={!editable} className="w-full border border-gray-200 rounded p-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <label className="text-[11px] text-gray-600 font-semibold block mb-1">{t("dailyEntry.testFuel")}</label>
                <input type="number" name="testFuel" value={diesel.testFuel || ''} onChange={handleInputChange(setDiesel)} disabled={!editable} className="w-full border border-gray-200 rounded p-1.5 text-xs outline-none focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400" />
              </div>
            </div>
          </div>
          
          <div className="bg-[#eff6ff] border-t border-blue-200 flex p-3 text-center">
            <div className="flex-1 border-r border-blue-200">
              <p className="text-[11px] text-gray-600 font-semibold mb-1">{t("dailyEntry.dieselSold")}</p>
              <p className="text-[15px] font-bold text-blue-600 whitespace-nowrap">{dData.sold > 0 ? dData.sold.toFixed(2) : "0.00"}</p>
            </div>
            <div className="flex-1">
              <p className="text-[11px] text-gray-600 font-semibold mb-1">{t("dailyEntry.dieselAmount")}</p>
              <p className="text-[15px] font-bold text-blue-600 whitespace-nowrap">{dData.amount > 0 ? dData.amount.toLocaleString('en-IN', {minimumFractionDigits: 2}) : "0.00"}</p>
            </div>
          </div>
        </div>

        {/* OIL CARD */}
          <div className="bg-white/70 backdrop-blur-2xl border border-white/50 rounded-[30px] shadow-[0_10px_40px_rgba(0,0,0,0.06)] overflow-hidden border-orange-200 overflow-hidden flex flex-col h-full">
            {/* HEADER */}
            <div className="px-4 py-3 flex items-center space-x-2 border-b border-orange-200 bg-[#fff7ed]">
              <BarChart3 className="w-5 h-5 text-orange-500" />
              <h3 className="text-[13px] font-bold text-orange-600 tracking-wide uppercase">
                {t("dailyEntry.oilPocketSystem")}
              </h3>
            </div>

            {/* BODY */}
            <div className="p-4 flex-grow">
              <table className="w-full border-collapse">
                {/* HEADERS */}
                <thead>
                  <tr className="border-b border-orange-100">
                    <th className="py-2 px-2 text-left text-[11px] font-bold text-gray-700 border-r border-orange-100">
                      Type
                    </th>
                    <th className="py-2 px-2 text-center text-[11px] font-bold text-gray-700 border-r border-orange-100">
                      20ml
                    </th>
                    <th className="py-2 px-2 text-center text-[11px] font-bold text-gray-700 border-r border-orange-100">
                      40ml
                    </th>
                    <th className="py-2 px-2 text-center text-[11px] font-bold text-gray-700">
                      60ml
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* CARRY FORWARD */}
                  <tr className="border-b border-orange-100">
                    <td className="py-2 px-2 text-[11px] font-semibold text-gray-600 border-r border-orange-100">
                      Carry Fwd
                    </td>
                    {["20ml", "40ml", "60ml"].map((size, index) => (
                      <td
                        key={size}
                        className={`text-center py-2 px-2 ${
                          index !== 2
                            ? "border-r border-orange-100"
                            : ""
                        }`}
                      >
                        <span className="text-[11px] font-bold text-gray-500">
                          {oil[size].carryFwd || 0}
                        </span>
                      </td>
                    ))}
                  </tr>

                  {/* ADDED */}
                  <tr className="border-b border-orange-100">

                    <td className="py-2 px-2 text-[11px] font-semibold text-gray-600 border-r border-orange-100">
                      Added
                    </td>

                    {["20ml", "40ml", "60ml"].map((size, index) => (

                      <td
                        key={size}
                        className={`text-center py-2 px-2 ${
                          index !== 2
                            ? "border-r border-orange-100"
                            : ""
                        }`}
                      >

                        <input
                          type="number"
                          onChange={handleOilChange(size, "add")}
                          value={oil[size].add || ""}
                          disabled={!editable}
                          className="w-14 h-8 border border-gray-200 rounded text-[10px] text-center outline-none focus:border-orange-500 disabled:bg-gray-50"
                        />

                      </td>

                    ))}

                  </tr>

                  {/* TOTAL */}
                  <tr className="border-b border-orange-100">

                    <td className="py-2 px-2 text-[11px] font-semibold text-gray-600 border-r border-orange-100">
                      Total
                    </td>

                    {["20ml", "40ml", "60ml"].map((size, index) => {

                      const totalOpen =
                        Number(oil[size].carryFwd || 0) +
                        Number(oil[size].add || 0);

                      return (

                        <td
                          key={size}
                          className={`text-center py-2 px-2 ${
                            index !== 2
                              ? "border-r border-orange-100"
                              : ""
                          }`}
                        >

                          <span className="text-[11px] font-bold text-orange-500">
                            {totalOpen}
                          </span>

                        </td>

                      );
                    })}

                  </tr>

                  {/* CLOSING */}
                  <tr>

                    <td className="py-2 px-2 text-[11px] font-semibold text-gray-600 border-r border-orange-100">
                      Closing
                    </td>

                    {["20ml", "40ml", "60ml"].map((size, index) => (

                      <td
                        key={size}
                        className={`text-center py-2 px-2 ${
                          index !== 2
                            ? "border-r border-orange-100"
                            : ""
                        }`}
                      >

                        <input
                          type="number"
                          onChange={handleOilChange(size, "close")}
                          value={oil[size].close || ""}
                          disabled={!editable}
                          className="w-14 h-8 border border-gray-200 rounded text-[10px] text-center outline-none focus:border-orange-500 disabled:bg-gray-50"
                        />

                      </td>

                    ))}

                  </tr>

                </tbody>

              </table>

            </div>

            {/* FOOTER */}
            <div className="bg-[#fff7ed] border-t border-orange-200 flex p-3 text-center">

              {/* SOLD PCS */}
              <div className="flex-1 border-r border-orange-200">

                <p className="text-[11px] text-gray-600 font-semibold mb-1">
                  Sold (Pcs)
                </p>

                <p className="text-[15px] font-bold text-orange-500 whitespace-nowrap">
                  {
                    (o20Data.sold || 0) +
                    (o40Data.sold || 0) +
                    (o60Data.sold || 0)
                  }
                </p>

              </div>

              {/* OIL AMOUNT */}
              <div className="flex-1">

                <p className="text-[11px] text-gray-600 font-semibold mb-1">
                  Oil Amount
                </p>

                <p className="text-[15px] font-bold text-orange-500 whitespace-nowrap">
                  {oilTotalAmount > 0
                    ? oilTotalAmount.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })
                    : "0.00"}
                </p>

              </div>

            </div>

          </div>
      

</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
        
        {/* PAYMENT CARD */}
        <div className="bg-white/70 backdrop-blur-2xl border border-white/50 rounded-[30px] shadow-[0_10px_40px_rgba(0,0,0,0.06)] overflow-hidden border-purple-200 overflow-hidden flex flex-col h-full">
          <div className="bg-[#f3e8ff] px-4 py-3 flex items-center space-x-2 border-b border-purple-200">
            <Wallet className="w-5 h-5 text-purple-600" />
            <h3 className="text-[13px] font-bold text-purple-700 tracking-wide uppercase">{t("dailyEntry.payment")}</h3>
          </div>
          <div className="p-5 flex-grow flex flex-col justify-center">
            <div className="grid grid-cols-2 gap-6 mb-4">
              <div>
                <label className="text-[11px] text-gray-600 font-semibold block mb-1.5">{t("dailyEntry.cashAmount")}</label>
                <input type="number" name="cash" value={payment.cash || ''} onChange={handleInputChange(setPayment)} disabled={!editable} className="w-full border border-gray-200 rounded p-2 text-xs outline-none focus:border-purple-500 disabled:bg-gray-50 disabled:text-gray-400" />
              </div>
              <div>
                <label className="text-[11px] text-gray-600 font-semibold block mb-1.5">{t("dailyEntry.onlineAmount")}</label>
                <input type="number" name="online" value={payment.online || ''} onChange={handleInputChange(setPayment)} disabled={!editable} className="w-full border border-gray-200 rounded p-2 text-xs outline-none focus:border-purple-500 disabled:bg-gray-50 disabled:text-gray-400" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-[11px] text-gray-600 font-semibold block mb-1.5">{t("dailyEntry.creditAmount") || "Credit Amount"}</label>
                <input type="number" name="credit" value={payment.credit || ''} onChange={handleInputChange(setPayment)} disabled={!editable} className="w-full border border-gray-200 rounded p-2 text-xs outline-none focus:border-purple-500 disabled:bg-gray-50 disabled:text-gray-400" />
              </div>
              <div>
                <label className="text-[11px] text-gray-600 font-semibold block mb-1.5">Receipt Amount (₹)</label>
                <input type="number" name="receipt" value={payment.receipt || ''} onChange={handleInputChange(setPayment)} disabled={!editable || !isMatched} className={`w-full border border-gray-200 rounded p-2 text-xs outline-none focus:border-purple-500 ${(!editable || !isMatched) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`} placeholder={!isMatched ? "Match sales total first" : ""} />
              </div>
            </div>

            {shift === "Day Shift" && intermediatePayment && (
              <div className="mt-2 pt-4 border-t border-purple-100">
                <button 
                  onClick={() => setShowInterPayment(!showInterPayment)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <label className="text-[11px] text-purple-700 font-semibold uppercase tracking-wide cursor-pointer">
                    Intermediate Shift Payment Details
                  </label>
                  {showInterPayment ? <ChevronUp className="w-4 h-4 text-purple-600" /> : <ChevronDown className="w-4 h-4 text-purple-600" />}
                </button>
                
                {showInterPayment && (
                  <div className="mt-3 bg-purple-50 rounded-lg p-3 grid grid-cols-2 gap-3 text-[11px] animate-in slide-in-from-top-2 duration-200">
                    <div>
                      <span className="text-gray-500 block mb-0.5">Cash</span>
                      <span className="font-bold text-gray-800">₹{intermediatePayment?.cash?.toLocaleString('en-IN', {minimumFractionDigits: 2}) || "0.00"}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block mb-0.5">Online</span>
                      <span className="font-bold text-gray-800">₹{intermediatePayment?.online?.toLocaleString('en-IN', {minimumFractionDigits: 2}) || "0.00"}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block mb-0.5">Credit</span>
                      <span className="font-bold text-gray-800">₹{intermediatePayment?.credit?.toLocaleString('en-IN', {minimumFractionDigits: 2}) || "0.00"}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block mb-0.5">Receipt</span>
                      <span className="font-bold text-gray-800">₹{(intermediatePayment?.receipt || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className="col-span-2 pt-2 mt-1 border-t border-purple-200 flex justify-between">
                      <span className="font-bold text-purple-700">Total Collection</span>
                      <span className="font-bold text-purple-700">₹{intermediateAmount?.toLocaleString('en-IN', {minimumFractionDigits: 2}) || "0.00"}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="bg-[#f3e8ff] p-4 border-t border-purple-200 flex justify-center items-center">
            <div className="text-center">
              <p className="text-[11px] text-gray-600 font-semibold mb-1">{t("dailyEntry.totalEntered")}</p>
              <p className="text-[16px] font-bold text-purple-700 whitespace-nowrap">{paymentTotal > 0 ? paymentTotal.toLocaleString('en-IN', {minimumFractionDigits: 2}) : "0.00"}</p>
            </div>
          </div>
        </div>

        {/* SUMMARY CARD */}
        <div className="bg-white/70 backdrop-blur-2xl border border-white/50 rounded-[30px] shadow-[0_10px_40px_rgba(0,0,0,0.06)] overflow-hidden border-red-200 overflow-hidden flex flex-col h-full">
          <div className="px-4 py-3 flex items-center space-x-2 border-b border-red-200 bg-[#fef2f2]">
            <BarChart3 className="w-5 h-5 text-red-500" />
            <h3 className="text-[13px] font-bold text-red-600 tracking-wide uppercase">{t("dailyEntry.summary")}</h3>
          </div>
          <div className="p-5 space-y-4 flex-grow">
            <div className="flex justify-between items-center text-[13px] font-medium border-b border-gray-100 pb-2">
              <span className="text-gray-700">{t("dailyEntry.petrolAmount")}</span>
              <span className="text-green-600 font-bold whitespace-nowrap ml-2">{pData.amount > 0 ? pData.amount.toLocaleString('en-IN', {minimumFractionDigits: 2}) : "0.00"}</span>
            </div>
            <div className="flex justify-between items-center text-[13px] font-medium border-b border-gray-100 pb-2">
              <span className="text-gray-700">{t("dailyEntry.dieselAmount")}</span>
              <span className="text-blue-600 font-bold whitespace-nowrap ml-2">{dData.amount > 0 ? dData.amount.toLocaleString('en-IN', {minimumFractionDigits: 2}) : "0.00"}</span>
            </div>
            <div className="flex justify-between items-center text-[13px] font-medium border-b border-gray-100 pb-2">
              <span className="text-gray-700">{t("dailyEntry.oilTotalAmount")}</span>
              <span className="text-orange-500 font-bold whitespace-nowrap ml-2">{oilTotalAmount > 0 ? oilTotalAmount.toLocaleString('en-IN', {minimumFractionDigits: 2}) : "0.00"}</span>
            </div>
            
            <div className="flex justify-between items-center pt-2">
              <span className="text-[13px] font-bold text-red-600">Sales Total</span>
              <span className="text-[16px] font-bold text-red-600 whitespace-nowrap ml-2">{salesTotal > 0 ? salesTotal.toLocaleString('en-IN', {minimumFractionDigits: 2}) : "0.00"}</span>
            </div>
            
            {Number(payment.receipt) > 0 && (
              <div className="flex justify-between items-center pt-1">
                <span className="text-[13px] font-bold text-gray-600">Receipt Amount</span>
                <span className="text-[14px] font-bold text-gray-600 whitespace-nowrap ml-2">+ {Number(payment.receipt).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
              </div>
            )}
            
            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
              <span className="text-[14px] font-bold text-purple-700">Final Collection</span>
              <span className="text-[18px] font-extrabold text-purple-700 whitespace-nowrap ml-2">{finalCollectionAmount > 0 ? finalCollectionAmount.toLocaleString('en-IN', {minimumFractionDigits: 2}) : "0.00"}</span>
            </div>

            {isMatched && salesTotal > 0 ? (
              <div className="mt-4 bg-[#f0fdf4] border border-green-300 text-green-700 px-4 py-2.5 rounded flex items-center space-x-2 transition-all">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-[12px] font-semibold">{t("dailyEntry.totalMatched")}</span>
              </div>
            ) : salesTotal > 0 ? (
              <div className="mt-4 bg-[#fef2f2] border border-red-300 text-red-700 px-4 py-2.5 rounded flex items-center space-x-2 transition-all">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-[12px] font-semibold">{t("dailyEntry.mismatch")}: ₹{Math.abs(salesTotal - paymentTotal).toLocaleString('en-IN', {minimumFractionDigits: 2})}</span>
              </div>
            ) : (
              <div className="mt-4 border border-transparent px-4 py-2.5"></div>
            )}
          </div>
        </div>

      </div>

      <div className="flex justify-center pt-4">
        <button 
          onClick={handleSave} 
          disabled={!isMatched || salesTotal === 0 || isSaving || !editable}
          className={`${(isMatched && salesTotal > 0 && !isSaving && editable) ? 'bg-green-700 hover:bg-green-800' : 'bg-gray-400 cursor-not-allowed'} text-white px-10 py-3 rounded text-[13px] font-bold flex items-center space-x-2 transition-colors duration-200 shadow-sm`}
        >
          {isSaving ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <Save className="w-5 h-5" />
          )}
          <span>{isSaving ? t("dailyEntry.saving") : t("dailyEntry.saveEntry")}</span>
        </button>
      </div>

      {shift === "Day Shift" && dailySummary && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg shadow-sm p-5 mt-8 mb-2">
          <h3 className="text-sm font-bold text-indigo-800 uppercase tracking-wide mb-4">Today's Sales Summary (Consolidated)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-[12px]">
            <div className="bg-white p-3 rounded border border-indigo-100 shadow-sm">
              <p className="font-bold text-gray-700 border-b border-gray-100 pb-1 mb-2">Petrol</p>
              <table className="w-full text-left text-[11px] mb-2">
                <thead><tr className="text-gray-500 border-b border-gray-50"><th></th><th>Open</th><th>Close</th><th className="text-right">Sold (L)</th></tr></thead>
                <tbody>
                  <tr><td className="text-gray-500 py-0.5">M1</td><td className="font-semibold">{dailySummary?.petrol?.m1?.opening || 0}</td><td className="font-semibold">{dailySummary?.petrol?.m1?.closing || 0}</td><td className="text-right font-semibold">{Number(dailySummary?.petrol?.m1?.sold || 0).toFixed(2)}</td></tr>
                  <tr><td className="text-gray-500 py-0.5">M2</td><td className="font-semibold">{dailySummary?.petrol?.m2?.opening || 0}</td><td className="font-semibold">{dailySummary?.petrol?.m2?.closing || 0}</td><td className="text-right font-semibold">{Number(dailySummary?.petrol?.m2?.sold || 0).toFixed(2)}</td></tr>
                  <tr><td className="text-red-500 py-0.5">Testing</td><td colSpan="2" className="text-[10px] text-gray-400 italic">(Day + Inter)</td><td className="text-right font-semibold text-red-500">- {dailySummary?.petrol?.testFuel || 0}</td></tr>
                </tbody>
              </table>
              <div className="flex justify-between mt-1 pt-1 border-t border-gray-50"><span className="text-indigo-600 font-bold">Total Sold:</span><span className="text-indigo-600 font-bold">{Number(dailySummary?.petrol?.totalSold || 0).toFixed(2)}</span></div>
            </div>
            <div className="bg-white p-3 rounded border border-indigo-100 shadow-sm">
              <p className="font-bold text-gray-700 border-b border-gray-100 pb-1 mb-2">Diesel</p>
              <table className="w-full text-left text-[11px] mb-2">
                <thead><tr className="text-gray-500 border-b border-gray-50"><th></th><th>Open</th><th>Close</th><th className="text-right">Sold (L)</th></tr></thead>
                <tbody>
                  <tr><td className="text-gray-500 py-0.5">M1</td><td className="font-semibold">{dailySummary?.diesel?.m1?.opening || 0}</td><td className="font-semibold">{dailySummary?.diesel?.m1?.closing || 0}</td><td className="text-right font-semibold">{Number(dailySummary?.diesel?.m1?.sold || 0).toFixed(2)}</td></tr>
                  <tr><td className="text-gray-500 py-0.5">M2</td><td className="font-semibold">{dailySummary?.diesel?.m2?.opening || 0}</td><td className="font-semibold">{dailySummary?.diesel?.m2?.closing || 0}</td><td className="text-right font-semibold">{Number(dailySummary?.diesel?.m2?.sold || 0).toFixed(2)}</td></tr>
                  <tr><td className="text-red-500 py-0.5">Testing</td><td colSpan="2" className="text-[10px] text-gray-400 italic">(Day + Inter)</td><td className="text-right font-semibold text-red-500">- {dailySummary?.diesel?.testFuel || 0}</td></tr>
                </tbody>
              </table>
              <div className="flex justify-between mt-1 pt-1 border-t border-gray-50"><span className="text-indigo-600 font-bold">Total Sold:</span><span className="text-indigo-600 font-bold">{Number(dailySummary?.diesel?.totalSold || 0).toFixed(2)}</span></div>
            </div>
            <div className="bg-white p-3 rounded border border-indigo-100 shadow-sm">
              <p className="font-bold text-gray-700 border-b border-gray-100 pb-1 mb-2">Oil (Pcs)</p>
              <table className="w-full text-left text-[11px] mb-2">
                <thead><tr className="text-gray-500 border-b border-gray-50"><th>Item</th><th>Open</th><th>Close</th><th className="text-right">Sold</th></tr></thead>
                <tbody>
                  <tr><td className="text-gray-500 py-0.5">20ml</td><td className="font-semibold">{dailySummary?.oil?.["20ml"]?.opening || 0}</td><td className="font-semibold">{dailySummary?.oil?.["20ml"]?.closing || 0}</td><td className="text-right font-semibold">{dailySummary?.oil?.["20ml"]?.sold || 0}</td></tr>
                  <tr><td className="text-gray-500 py-0.5">40ml</td><td className="font-semibold">{dailySummary?.oil?.["40ml"]?.opening || 0}</td><td className="font-semibold">{dailySummary?.oil?.["40ml"]?.closing || 0}</td><td className="text-right font-semibold">{dailySummary?.oil?.["40ml"]?.sold || 0}</td></tr>
                  <tr><td className="text-gray-500 py-0.5">60ml</td><td className="font-semibold">{dailySummary?.oil?.["60ml"]?.opening || 0}</td><td className="font-semibold">{dailySummary?.oil?.["60ml"]?.closing || 0}</td><td className="text-right font-semibold">{dailySummary?.oil?.["60ml"]?.sold || 0}</td></tr>
                </tbody>
              </table>
              <div className="flex justify-between mt-1 pt-1 border-t border-gray-50"><span className="text-indigo-600 font-bold">Total Sold:</span><span className="text-indigo-600 font-bold">{dailySummary?.oil?.totalSold || 0}</span></div>
            </div>
            <div className="bg-white p-3 rounded border border-indigo-100 shadow-sm">
              <p className="font-bold text-gray-700 border-b border-gray-100 pb-1 mb-2">Payments</p>
              <div className="flex justify-between text-[11px]"><span className="text-gray-500">Cash:</span><span className="font-semibold whitespace-nowrap ml-2">₹{Number(dailySummary?.payments?.cashTotal || 0).toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between text-[11px]"><span className="text-gray-500">Online/UPI:</span><span className="font-semibold whitespace-nowrap ml-2">₹{Number(dailySummary?.payments?.onlineTotal || 0).toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between text-[11px]"><span className="text-gray-500">Credit:</span><span className="font-semibold whitespace-nowrap ml-2">₹{Number(dailySummary?.payments?.creditTotal || 0).toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between text-[11px]"><span className="text-gray-500">Receipt:</span><span className="font-semibold whitespace-nowrap ml-2">₹{Number(dailySummary?.payments?.receiptTotal || 0).toLocaleString('en-IN')}</span></div>
              <div className="flex justify-between mt-1 pt-1 border-t border-gray-50"><span className="text-indigo-600 font-bold">Final Collection:</span><span className="text-indigo-600 font-bold whitespace-nowrap ml-2">₹{Number(dailySummary?.payments?.finalCollectionTotal || 0).toLocaleString('en-IN')}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* RECENT ENTRIES */}
      {showRecent && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mt-8">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
            <div className="flex items-center space-x-2">
              <History className="w-5 h-5 text-gray-400" />
              <h3 className="text-[14px] font-bold text-gray-700 uppercase tracking-wider">{t("dailyEntry.recentEntries")}</h3>
            </div>
            <span className="hidden sm:block text-[11px] text-gray-400 font-medium italic">{t("dailyEntry.clickToLoad")}</span>
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse text-[12px] whitespace-nowrap">
              <thead>
                <tr className="bg-gray-50 text-gray-500 uppercase text-[10px] font-bold tracking-widest border-b border-gray-100">
                  <th className="px-6 py-3">{t("common.date")}</th>
                  <th className="px-6 py-3">{t("common.shift")}</th>
                  <th className="px-6 py-3 text-green-600">{t("common.petrol")} (L)</th>
                  <th className="px-6 py-3 text-blue-600">{t("common.diesel")} (L)</th>
                  <th className="px-6 py-3 text-red-600">Sales Total</th>
                  <th className="px-6 py-3 text-red-600">Final Collection</th>
                  <th className="px-6 py-3 text-gray-400">{t("dailyEntry.savedBy")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentEntries.map((entry) => (
                  <tr key={entry._id}
                    onClick={() => setShowConfirmModal({ show: true, type: "load", pendingValue: entry })}
                    className="hover:bg-blue-50 cursor-pointer transition-colors group"
                  >
                    <td className="px-6 py-3 font-semibold text-gray-700">{new Date(entry.date).toLocaleDateString('en-GB')}</td>
                    <td className="px-6 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${entry.shift === 'Day Shift' ? 'bg-orange-50 text-orange-600 border border-orange-100' : 'bg-indigo-50 text-indigo-600 border border-indigo-100'}`}>
                        {entry.shift}
                      </span>
                    </td>
                    <td className="px-6 py-3 font-medium text-green-600">{(entry.petrol?.sold || 0).toFixed(2)}</td>
                    <td className="px-6 py-3 font-medium text-blue-600">{(entry.diesel?.sold || 0).toFixed(2)}</td>
                    <td className="px-6 py-3 font-bold text-gray-800">₹{((entry.totals?.salesTotal) || (entry.totals?.grandTotal) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-6 py-3 font-bold text-gray-800">₹{((entry.totals?.finalCollectionAmount) || (entry.totals?.finalCollectionAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-6 py-3 text-gray-400 text-[11px] group-hover:text-gray-600">{entry.updatedBy || entry.createdBy || 'System'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden divide-y divide-gray-50">
            {recentEntries.slice(0, 10).map((entry) => (
              <div
                key={entry._id}
                onClick={() => setShowConfirmModal({ show: true, type: "load", pendingValue: entry })}
                className="p-4 hover:bg-blue-50 active:bg-blue-100 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[13px] font-bold text-gray-800">
                    {new Date(entry.date).toLocaleDateString('en-GB')}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${entry.shift === 'Day Shift' ? 'bg-orange-50 text-orange-600 border border-orange-100' : 'bg-indigo-50 text-indigo-600 border border-indigo-100'}`}>
                    {entry.shift === 'Day Shift' ? '☀️ ' + entry.shift : '🌙 ' + entry.shift}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-green-50 rounded-lg py-1.5">
                    <p className="text-[9px] font-bold text-green-600 uppercase">{t("common.petrol")}</p>
                    <p className="text-[12px] font-bold text-green-700">{(entry.petrol?.sold || 0).toFixed(2)} L</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg py-1.5">
                    <p className="text-[9px] font-bold text-blue-600 uppercase">{t("common.diesel")}</p>
                    <p className="text-[12px] font-bold text-blue-700">{(entry.diesel?.sold || 0).toFixed(2)} L</p>
                  </div>
                  <div className="bg-red-50 rounded-lg py-1.5">
                    <p className="text-[9px] font-bold text-red-500 uppercase">Total</p>
                    <p className="text-[11px] font-bold text-red-600">₹{((entry.totals?.salesTotal) || (entry.totals?.grandTotal) || 0).toLocaleString('en-IN')}</p>
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5 text-right">{t("dailyEntry.savedBy")}: {entry.updatedBy || entry.createdBy || 'System'}</p>
              </div>
            ))}
          </div>
          
          {/* Pagination Controls */}
          {totalCount > 0 && (
            <div className="px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between bg-white space-y-3 sm:space-y-0">
              <div className="flex items-center text-xs text-gray-500">
                <span>Showing {(page - 1) * limit + 1} - {Math.min(page * limit, totalCount)} of {totalCount} entries</span>
                <select 
                  value={limit} 
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  className="ml-3 border border-gray-200 rounded px-2 py-1 outline-none"
                >
                  <option value={4}>4</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="flex items-center space-x-1">
                <button 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-2.5 py-1 border border-gray-200 rounded text-xs font-semibold text-gray-600 disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Prev
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                      page === pageNum
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}

                <button 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-2.5 py-1 border border-gray-200 rounded text-xs font-semibold text-gray-600 disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* IN-APP CONFIRMATION MODAL */}
     
     {showConfirmModal.show && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-[4px] transition-all duration-300">

        {/* iOS CONFIRMATION CARD */}
        <div
          className="
            w-full
            max-w-[400px]
            mx-4
            rounded-[28px]
            bg-white
            border
            border-gray-200/80
            shadow-[0_20px_60px_rgba(0,0,0,0.18)]
            overflow-hidden
            animate-in
            fade-in
            zoom-in
            duration-300
          "
        >

          {/* CONTENT */}
          <div className="px-7 pt-7 pb-6">

            {/* HEADER */}
            <div className="flex items-center gap-4 mb-5">

              {/* ICON */}
              <div
                className="
                  w-14
                  h-14
                  rounded-[20px]
                  bg-[#FFF7E8]
                  border
                  border-[#FFE7B8]
                  flex
                  items-center
                  justify-center
                  shadow-[inset_0_1px_1px_rgba(255,255,255,0.7)]
                  shrink-0
                "
              >

                <AlertCircle className="w-6 h-6 text-amber-500" />

              </div>

              {/* TITLE + DESCRIPTION */}
              <div className="flex-1">

                <h3 className="text-[22px] font-semibold tracking-tight text-gray-900">
                  {t("dailyEntry.confirmAction")}
                </h3>

                <p className="mt-2 text-[14px] leading-[1.7] text-gray-600">

                  {showConfirmModal.type === "save"
                    ? t("dailyEntry.saveConfirmText")
                    : showConfirmModal.type === "load"
                    ? t("dailyEntry.loadConfirmText")
                    : t("dailyEntry.changeConfirmText").replace(
                        "{type}",
                        t(`common.${showConfirmModal.type}`)
                      )}

                </p>

              </div>

            </div>

          </div>

          {/* FOOTER */}
          <div className="px-7 pb-7 flex gap-3">

            {/* CANCEL */}
            <button
              onClick={() =>
                setShowConfirmModal({
                  show: false,
                  type: "",
                  pendingValue: null,
                })
              }
              className="
                flex-1
                h-12
                rounded-[18px]
                bg-[#F3F4F6]
                text-sm
                font-semibold
                text-gray-700
                transition-all
                duration-300
                hover:bg-[#E5E7EB]
                active:scale-[0.98]
              "
            >
              {t("common.cancel")}
            </button>

            {/* CONFIRM */}
            <button
              onClick={handleConfirmModalAction}
              className="
                flex-1
                h-12
                rounded-[18px]
                bg-emerald-500
                text-sm
                font-semibold
                text-white
                shadow-[0_10px_30px_rgba(16,185,129,0.25)]
                transition-all
                duration-300
                hover:bg-emerald-600
                active:scale-[0.98]
              "
            >
              {t("common.yes")}, {t("common.continue")}
            </button>

          </div>

        </div>

      </div>
    )}
    </div>
    
  );
};

export default DailyEntry;
